// Shared schedule model for the Create and Manage job flows — both render
// the same Daily/Weekly/Monthly StepSchedule UI, so the conversions between
// that UI's state and a Quartz cron expression (the backend's actual
// representation) live here once instead of being duplicated per page.

export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const WEEKDAY_TO_QUARTZ: Record<string, string> = {
    Mon: 'MON',
    Tue: 'TUE',
    Wed: 'WED',
    Thu: 'THU',
    Fri: 'FRI',
    Sat: 'SAT',
    Sun: 'SUN'
};

const QUARTZ_TO_WEEKDAY: Record<string, string> = Object.fromEntries(
    Object.entries(WEEKDAY_TO_QUARTZ).map(([day, quartz]) => [quartz, day])
);

const MONTHDAY_OPTIONS = ['1st', '15th', '28th', 'Last'];

function pad(n: number): string {
    return String(n).padStart(2, '0');
}

export function computeNextRuns(
    frequency: string,
    weekdays: Record<string, boolean>,
    monthday: string,
    time: string
): string[] {
    const base = new Date();
    const [h, m] = time.split(':').map(Number);

    const fmt = (d: Date) =>
        d.toLocaleDateString('en-GB', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        }) +
        ' - ' +
        time;

    if (frequency === 'Daily') {
        return [1, 2, 3].map((i) => {
            const d = new Date(base);
            d.setDate(d.getDate() + i);
            d.setHours(h, m);
            return fmt(d);
        });
    }

    if (frequency === 'Weekly') {
        const dayMap: Record<string, number> = {
            Mon: 1,
            Tue: 2,
            Wed: 3,
            Thu: 4,
            Fri: 5,
            Sat: 6,
            Sun: 0
        };
        const selected = Object.entries(weekdays)
            .filter(([, v]) => v)
            .map(([k]) => dayMap[k]);
        if (!selected.length) return ['—', '—', '—'];
        const runs: string[] = [];
        const d = new Date(base);
        while (runs.length < 3) {
            d.setDate(d.getDate() + 1);
            if (selected.includes(d.getDay())) {
                const copy = new Date(d);
                copy.setHours(h, m);
                runs.push(fmt(copy));
            }
        }
        return runs;
    }

    // Monthly
    const dayNum =
        monthday === '1st'
            ? 1
            : monthday === '15th'
            ? 15
            : monthday === '28th'
            ? 28
            : -1;
    return [1, 2, 3].map((i) => {
        const d = new Date(base);
        d.setMonth(d.getMonth() + i);
        d.setDate(dayNum === -1 ? 0 : dayNum);
        d.setHours(h, m);
        return fmt(d);
    });
}

export function toQuartzCron(
    frequency: string,
    weekdays: Record<string, boolean>,
    monthday: string,
    time: string
): string {
    const [h, m] = time.split(':');

    if (frequency === 'Daily') {
        return `0 ${m} ${h} * * ?`;
    }

    if (frequency === 'Weekly') {
        const days = Object.entries(weekdays)
            .filter(([, v]) => v)
            .map(([k]) => WEEKDAY_TO_QUARTZ[k])
            .join(',');
        return `0 ${m} ${h} ? * ${days}`;
    }

    // Monthly
    const day = monthday === 'Last' ? 'L' : monthday.replace(/\D/g, '');
    return `0 ${m} ${h} ${day} * ?`;
}

export interface ParsedSchedule {
    frequency: string;
    weekdays: Record<string, boolean>;
    monthday: string;
    time: string;
}

const NO_WEEKDAYS = Object.fromEntries(WEEKDAYS.map((d) => [d, false]));

// Best-effort inverse of toQuartzCron, for hydrating the Daily/Weekly/Monthly
// UI from a config's stored cron expression. This UI only models a subset of
// Quartz (no hourly/interval crons, no arbitrary day-of-month), so anything
// outside that subset — including cron strings from the older free-form
// CronPicker — falls back to a sane Daily default rather than erroring.
export function parseQuartzCron(cron: string): ParsedSchedule {
    const fallback: ParsedSchedule = {
        frequency: 'Daily',
        weekdays: { ...NO_WEEKDAYS, Mon: true },
        monthday: '1st',
        time: '08:00'
    };

    const parts = cron.trim().split(/\s+/);
    if (parts.length !== 6) return fallback;
    const [, min, hour, dom, , dow] = parts;

    if (!/^\d+$/.test(min) || !/^\d+$/.test(hour)) return fallback;
    const time = `${pad(Number(hour))}:${pad(Number(min))}`;

    if (dom === '?' && dow && dow !== '?' && dow !== '*') {
        const expanded = dow.includes('-')
            ? (() => {
                  const order = Object.values(WEEKDAY_TO_QUARTZ);
                  const [start, end] = dow.split('-');
                  const startIdx = order.indexOf(start);
                  const endIdx = order.indexOf(end);
                  if (startIdx === -1 || endIdx === -1) return [];
                  return order.slice(startIdx, endIdx + 1);
              })()
            : dow.split(',');
        const weekdays = { ...NO_WEEKDAYS };
        let matched = false;
        expanded.forEach((q) => {
            const day = QUARTZ_TO_WEEKDAY[q.toUpperCase()];
            if (day) {
                weekdays[day] = true;
                matched = true;
            }
        });
        if (matched)
            return { frequency: 'Weekly', weekdays, monthday: '1st', time };
    }

    if (/^\d+$/.test(dom) || dom === 'L') {
        const monthday =
            dom === 'L'
                ? 'Last'
                : MONTHDAY_OPTIONS.find((o) => o.replace(/\D/g, '') === dom) ||
                  '1st';
        return {
            frequency: 'Monthly',
            weekdays: fallback.weekdays,
            monthday,
            time
        };
    }

    if (dom === '*' || dom === '?') {
        return {
            frequency: 'Daily',
            weekdays: fallback.weekdays,
            monthday: '1st',
            time
        };
    }

    return fallback;
}
